import React, { useState, useEffect } from "react";

import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { Paragraph } from "@project-stories/elements/Paragraph/Paragraph";
import { BlogArticleTile } from "@project-stories/blog/BlogArticleTile/BlogArticleTile";
import { Button } from "@project-stories/elements/Button/Button";
import { Headings } from "@arctheme-components/elements/Headings/Headings";

const GridLayout = ({ initialArticles, settings }) => {
  const { section, blocks, globals } = settings;

  const articleBlocks = blocks?.filter((block) => block.type === "article_block");
  const initialPerPage = globals.articles_per_page || 3;
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage] = useState(initialPerPage);
  const [articles, setArticles] = useState(articleBlocks || initialArticles|| []);
  const [currentArticles, setCurrentArticles] = useState(
    articleBlocks?.slice(0, articlesPerPage) || initialArticles?.slice(0, articlesPerPage) || [],
  );

  const contentBlock = blocks?.find((block) => block.type === "content_block");
  
  const showPagination = section.show_pagination !== false;
  const taglineText = contentBlock?.settings?.tagline;
  const titleText = contentBlock?.settings?.title;
  const taglineType = contentBlock?.settings?.tagline_type || "h6";
  const titleType = contentBlock?.settings?.title_type || "h2";
  const showMetadata = section.show_metadata !== false;
  const imageRounding = section.image_rounding || "none";

  useEffect(() => {
    const handleArticlesComplete = (event) => {
      setCurrentPage(1);
      setArticles(event.detail.articles);
    };

    const handleNextPage = (event) => {
      if (event.detail.type === "pagination") {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    };

    window.addEventListener("blog-articles-complete", handleArticlesComplete);
    window.addEventListener("nextpage", handleNextPage);

    return () => {
      window.removeEventListener(
        "blog-articles-complete",
        handleArticlesComplete,
      );
      window.removeEventListener("nextpage", handleNextPage);
    };
  }, []);

  useEffect(() => {
    const indexOfLastArticle = currentPage * articlesPerPage;
    const newArticles = articles.slice(0, indexOfLastArticle);
    setCurrentArticles(newArticles);
  }, [currentPage, articles, articlesPerPage]);

  const contentBlockRender = () => (
    <div className="flex flex-col space-y-4 mb-8 text-center">
      {taglineText && (
        <Paragraph
          text={taglineText}
          type={taglineType}
          classes=""
        />
      )}
      {titleText && (
        <Headings
          text={titleText}
          type={titleType}
          classes="font-bold"
        />
      )}
      {settings?.section?.subtext && (
        <Paragraph
          text={settings?.section?.subtext}
          type={settings?.section?.subtext_type}
        />
      )}
    </div>
  );

  const isReverse = isMobile
    ? section.layout_type_mobile === 'grid_reverse'
    : section.layout_type === 'grid_reverse';

  return (
    <div className={`w-full color-${section.color_schema}`}>
      <div className={"flex flex-col items-center mb-8"}>
        <div className={"section-toprow__details"}>
          {contentBlockRender()}
        </div>
        <div className={"flex-item flex"}>
          {contentBlock?.settings.cta_text && (
            <Button
              link={contentBlock?.settings.cta_url || "/blogs"}
              label={contentBlock?.settings.cta_text || "Read All Articles"}
              style={contentBlock?.settings.cta_style || "primary"}
            />
          )}
        </div>
      </div>

      <div
        className="blog-articles flex flex-col"
        x-data="pagination"
        x-init={`init(${articles.length}, ${articlesPerPage}, true, 'articles')`}
      >
        {currentArticles.length > 0 && (
          <>
            <div className={`article-items gap-[8px] lg:gap-[20px]`}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-[30px] gap-y-8 lg:gap-y-[60px]">
                {currentArticles.map((article, index) => {
                  return (
                    <div
                      className="blog-articles__article article"
                      key={index}
                    >
                      <BlogArticleTile
                        article={article.article || article}
                        show_excerpt={true}
                        settings={settings}
                        showContent={false}
                        showCategory={true}
                        showMetadata={true}
                        imageRounding={imageRounding}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
            {showPagination && (
              <div
                x-ref="paginationContainer"
                className="flex justify-center items-center space-x-4 pt-[64px] lg:pt-[87px]"
              ></div>
            )}
          </>
        )}
        {currentArticles.length <= 0 && (
          <div className={"blog-articles-empty"}>
            <Paragraph
              text={"There are no articles found with that filter."}
              type={"b2"}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GridLayout;
